/**
 * Issue #617 — what a Grounds Directory search actually FOUND, said where the
 * search was typed.
 *
 * THE BUG THIS EXISTS FOR. The directory search has always worked: the page
 * filters client-side and renders every match as a card. It renders them in
 * `grounds-directory-grid`, the LAST block on `/grounds-map`, underneath a
 * 60vh map that itself sits under a header, a filter card, a hint line and
 * three legend rows. On a 1184px laptop the grid starts below the fold; on a
 * phone, far below it. So the only feedback a search produced was the map
 * flying somewhere — and Martin reported, twice, that searching a ground "does
 * not appear as a match, the only thing that happens is the map zooms in".
 *
 * Same class as #613: the thing you pressed produced something off-screen.
 *
 * Two answers, both driven from here so both are testable without mounting a
 * component (this repo mounts none in unit tests):
 *
 *   1. `searchResultSummary` — the sentence that goes directly under the
 *      search box: how many matched and which ones, or that none did.
 *   2. `mapBlockOrderClass` — the flex `order` that moves the MAP (and its
 *      legend furniture) BELOW the result grid on a narrow viewport while a
 *      search has hits. The map is context; the list is the answer, and the
 *      answer belongs where the question was typed.
 * The map keeps its OWN no-match notice (#212) as well. An earlier cut of this
 * fix suppressed it to avoid saying the same thing twice; Journey 1 rejected
 * that, and it was right to. The map notice exists so that someone looking at
 * an empty map learns why it is empty *in the place their eye already is* —
 * and at phone width the map is below the fold, so the line under the search
 * box cannot serve that reader. Duplication was never the defect. The defect
 * was that the only copy was 224px past the bottom of the screen.
 *
 * WHY THE ZERO CASE IS HERE, having first been left out. The first cut of
 * this file returned `null` for a search that matched nothing, on the
 * reasoning that `mapNotice` in GroundsMap.jsx already draws "No grounds match
 * X" on the map surface and one message per state beats two. Journey 1b then
 * measured that notice on a 390x844 phone: it ends at **1067px**, 224px below
 * the fold. The map notice is pinned to the top of the map, and the map is
 * itself below a header, a filter card that wraps to five rows at 390px, and
 * the legend block — so "there are no matches" was landing a screen and a
 * quarter down, in the same place, for the same reason, as the match list this
 * issue exists to raise. The zero answer is an answer. It gets the same
 * treatment as the others, and the map notice yields to it.
 */

/** How many names the line spells out before it starts counting. */
export const SUMMARY_PREVIEW = 3;

/**
 * The line under the search box, or `null` when the box is empty.
 *
 * `matches` is the page's `filtered` — the search NARROWED by the source and
 * type chips and by near-me, i.e. exactly the set the cards below render and
 * the map fits to. Summarising `searched` instead would advertise a count the
 * grid does not show, which is the same species of lie as the "All grounds
 * (1)" select label this replaces.
 *
 * `kind` is `"some"` or `"none"`. Callers branch on it rather than on
 * `count`, so that "no search at all" (null) stays distinct from "a search
 * that found nothing" — conflating those two is what put the zero answer off
 * the bottom of the page in the first place.
 *
 * ## ISSUE #644 — `total` and `partial`, because the answer is now PAGED
 *
 * Counting `matches` was exactly right while the client held every match. It
 * stopped being right the moment the server started paging: the rows in hand
 * are 100 of 350, and counting them reports 100 matches over a directory
 * holding 350 — the under-reporting twin of the "All grounds (1)" lie this
 * file was written to replace.
 *
 * So the caller may say what it knows, and the two options are deliberately
 * not one:
 *
 *   `total`   — the TRUE number of matches, from the server. Passed only when
 *               it still describes what is on screen, i.e. when nothing on the
 *               client has narrowed the answer. The named preview still comes
 *               off the rows in hand, and the tail becomes "and 347 more",
 *               which is the honest sentence.
 *   `partial` — "the rows in hand are not all of them, and I cannot say how
 *               many there are". A chip narrowing a multi-page search is
 *               exactly that: later pages may hold more rows the chip would
 *               keep, and this client would have to fetch them to know. The
 *               headline then says `12 matches on this page`.
 *
 * Passing neither is the pre-#644 behaviour, byte for byte, which is what a
 * single-page search still gets — and what Journey 1b measures.
 */
export function searchResultSummary(term, matches, options = {}) {
  const trimmed = typeof term === "string" ? term.trim() : "";
  if (!trimmed) return null;
  const rows = Array.isArray(matches) ? matches : [];
  const total = options.total;
  // The rows in hand are the floor: a `total` smaller than what is on screen
  // is a stale answer, and reporting it would put a count under a longer list.
  const count = typeof total === "number" && Number.isFinite(total)
    ? Math.max(total, rows.length)
    : rows.length;

  if (rows.length === 0) {
    return {
      kind: "none",
      term: trimmed,
      count: 0,
      names: [],
      remaining: 0,
      headline: "No matches",
      detail: `Nothing in your directory matches “${trimmed}”.`,
    };
  }

  const preview = options.preview ?? SUMMARY_PREVIEW;
  const names = rows
    .slice(0, preview)
    .map((g) => (typeof g?.name === "string" ? g.name.trim() : ""))
    .filter(Boolean);
  const remaining = count - names.length;

  return {
    kind: "some",
    term: trimmed,
    count,
    names,
    remaining,
    // #644: the scope, said out loud when the count is this page's rather
    // than the directory's. A bare "12 matches" over page 1 of 4 is the
    // per-page count #612 was filed for.
    headline: options.partial
      ? `${count} match${count === 1 ? "" : "es"} on this page`
      : `${count} match${count === 1 ? "" : "es"}`,
    detail: detailFor(names, remaining),
  };
}

/**
 * The named matches, then the tail as a number. A directory search that hits
 * forty grounds must not try to read forty names out above the map — the count
 * is the fact, the first few names are the recognition.
 */
function detailFor(names, remaining) {
  if (names.length === 0) return remaining > 0 ? `${remaining} more` : "";
  if (remaining <= 0) return names.join(", ");
  return `${names.join(", ")} and ${remaining} more`;
}

/**
 * The Tailwind `order` for the map block — the hint, the legends and the map
 * card — given the current summary.
 *
 * `order-1` pushes it past every default (`order-0`) sibling, which is what
 * puts the result grid above it; `md:order-none` returns the page to DOM order
 * from 768px up, where the whole layout already fits and the map leading is
 * the point of the page.
 *
 * ONLY for `kind: "some"`. With nothing found there is no grid to promote, and
 * reordering around an empty one would move the map for no reason. Returns ""
 * when no search is active, so an untouched directory renders exactly as it
 * did before this issue. Both literals are spelled out in full because
 * Tailwind's content scanner reads source text, not runtime strings.
 */
export function mapBlockOrderClass(summary) {
  return summary?.kind === "some" ? "order-1 md:order-none" : "";
}

/**
 * Scroll the result grid into view, tolerating an element that is missing or
 * has no `scrollIntoView` (jsdom does not implement it — see AdminVenues.jsx,
 * which learned this the hard way in #613).
 */
export function scrollResultsIntoView(el) {
  if (!el || typeof el.scrollIntoView !== "function") return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}
